import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import {
  type CallPayload,
  endSupportCall,
  startSupportCall,
  useReplyTicket,
  useSupportSubscription,
  useTicket,
} from "@/lib/support";
import { SupportCallRoom } from "@/modules/support/SupportCallRoom";
import { TicketStatusPill } from "@/modules/support/ticketStatus";
import { TicketThread } from "@/modules/support/TicketThread";

export default function SupportTicketDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const { data, isLoading } = useTicket(id);
  const { data: sub } = useSupportSubscription();
  const reply = useReplyTicket(id);
  const [draft, setDraft] = useState("");
  const [call, setCall] = useState<CallPayload | null>(null);
  const [callErr, setCallErr] = useState<string | null>(null);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    await reply.mutateAsync(draft);
    setDraft("");
  };

  const beginCall = async () => {
    setCallErr(null);
    try {
      setCall(await startSupportCall(id));
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setCallErr(detail ?? t("support.detail.callError"));
    }
  };

  const leaveCall = async () => {
    if (call) await endSupportCall(call.session_id).catch(() => {});
    setCall(null);
  };

  if (isLoading || !data) {
    return (
      <AppLayout title={t("support.detail.title")}>
        <p className="text-nb-text-2">{t("common.loading")}</p>
      </AppLayout>
    );
  }
  const { ticket, messages } = data;
  const canCall = !!sub?.plan.allows_live_call;

  return (
    <AppLayout title={ticket.subject}>
      {call && <SupportCallRoom call={call} onLeave={leaveCall} />}

      <BackLink fallback="/support" className="text-sm text-cvp-accent">
        ← {t("support.detail.back")}
      </BackLink>
      <div className="flex items-start justify-between gap-4">
        <div className="text-xs text-nb-text-2">
          #{ticket.id.slice(0, 8)} · {t(`support.category.${ticket.category}`)} ·{" "}
          {t(`support.priority.${ticket.priority}`)}
        </div>
        <TicketStatusPill status={ticket.status} />
      </div>

      {canCall && ticket.status !== "closed" && (
        <div className="card flex items-center justify-between">
          <div className="text-sm text-nb-text-2">{t("support.detail.startCallHint")}</div>
          <button className="btn-primary" onClick={beginCall}>
            {t("support.detail.startCall")}
          </button>
        </div>
      )}
      {callErr && <p className="text-sm text-cvp-danger">{callErr}</p>}

      <TicketThread messages={messages} />

      {ticket.status === "closed" ? (
        <p className="text-sm text-nb-text-2">{t("support.detail.closedNotice")}</p>
      ) : (
        <form onSubmit={send} className="space-y-2">
          <textarea
            className="input"
            rows={3}
            placeholder={t("support.detail.replyPlaceholder")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit" className="btn-primary" disabled={reply.isPending}>
            {reply.isPending ? t("support.detail.sending") : t("support.detail.sendReply")}
          </button>
        </form>
      )}
    </AppLayout>
  );
}
